import {useEffect, useRef, useState} from 'react';
import {fenzsafeStore, FENZSAFE_DOCUMENT_MAX_BYTES, Matter} from './fenzsafeStore';
import {friendlyMutationError} from './friendlyMutationError';
import {DiscoveryCandidate, DiscoveryScanStats, isDirectoryDiscoverySupported, scanDirectoryForCandidates} from './fenzsafeDiscovery';

type Props = {
  matter: Matter;
};

type Status =
  | {step: 'idle'}
  | {step: 'scanning'; filesExamined: number}
  | {step: 'reviewing'; folderName: string; candidates: DiscoveryCandidate[]; truncated: boolean; stats: DiscoveryScanStats}
  | {step: 'importing'; folderName: string; total: number; completed: number; currentName: string}
  | {step: 'done'; selected: number; imported: number; skipped: number; stats: DiscoveryScanStats};

const BAND_LABEL: Record<DiscoveryCandidate['band'], string> = {strong: 'Strong match', possible: 'Possible match'};

/** Plain, local-only summary of a scan's own metrics (founder review comment 5729176495) -- shown to the person who ran the search, never sent anywhere. Never mentions a filename, path, or anything about a specific candidate. */
function formatScanStats(stats: DiscoveryScanStats): string {
  const seconds = (stats.elapsedMs / 1000).toFixed(1);
  const filesLabel = `${stats.filesExamined} file${stats.filesExamined === 1 ? '' : 's'} examined`;
  const firstCandidateNote = stats.timeToFirstCandidateMs !== null ? `, first match at ${(stats.timeToFirstCandidateMs / 1000).toFixed(1)}s` : '';
  return `Search took ${seconds}s -- ${filesLabel}${firstCandidateNote}.`;
}

/**
 * FenzSafe "Find related files" discovery -- Slice 1A (Issue #114 founder product requirement,
 * comment 5726376684; feasibility contract at docs/ai/FENZSAFE_DISCOVERY_FEASIBILITY_CONTRACT.md;
 * scoped per the founder's review/corrections at comments 5727903903, 5729176495, and 5730113935).
 *
 * Browser-selected-folder discovery only (Mode A of the contract) -- no desktop companion.
 * Matching is filename-based plus, for TXT/CSV/PDF/EML/DOCX files within fenzsafeDiscovery.ts's
 * own per-file and cumulative size budgets (DOCX additionally gated on a ZIP archive preflight --
 * fenzsafeArchivePreflight.ts -- passing first), a direct read of the file's own text content,
 * plus (founder review comment 5729176495) a plain "this file's date lines up with this matter's
 * other activity" signal built only from the matter's own already-real timestamps. XLSX/MSG/OCR/
 * general-archive extraction and cloud/AI scoring remain deferred to their own later,
 * separately-reviewed slices. This is local, deterministic keyword/substring/date-range matching,
 * not an AI or semantic model, and is never described as either -- the date signal in particular
 * is never framed as legal significance or an inference, only a plain date-range fact. This
 * component never reads a candidate file's bytes at all until fenzsafeDiscovery.ts's own scan has
 * already decided it is a match; nothing about it is recorded anywhere until the user explicitly
 * selects it for import. Scan-level metrics (files examined, candidates found, elapsed time) are
 * computed and shown locally in this component's own UI only -- never sent anywhere, and never
 * carrying a filename, path, or file content.
 *
 * Import reuses the exact existing FenzSafe upload contract (fenzsafeStore.addDocument() +
 * registerDocumentBytes()) unchanged -- the same real byte-registration -> quarantine/scan ->
 * accepted pipeline every other FenzSafe upload path already goes through, so no new backend
 * surface, tenant-isolation seam, or audit event type was needed to build this. Discovery itself
 * (the scan and the review list) touches no FenzSafe backend endpoint at all -- it runs entirely
 * against the File System Access API in this browser tab (contract §2's local-vs-server
 * boundary), and nothing about a candidate is ever recorded as part of the matter until the user
 * explicitly imports it (contract's "discovery is not custody" boundary). Only a
 * folder-relative path is ever produced or shown -- never the file's real absolute path on disk,
 * and never anything sent to an attorney-share recipient.
 */
export function FenzSafeDiscoveryPanel({matter}: Props) {
  const [status, setStatus] = useState<Status>({step: 'idle'});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [pickerRecovery, setPickerRecovery] = useState(false);
  const scanControllerRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  // Updated synchronously on every render (not inside an effect) so an in-flight async
  // operation started under a previous matter can detect the switch the moment this component
  // re-renders with a new matter -- before the reset effect below even runs, and regardless of
  // whether that operation's own promise (e.g. the native folder picker, which takes no abort
  // signal) can itself be cancelled.
  const matterIdRef = useRef(matter.id);
  matterIdRef.current = matter.id;
  // A monotonic token for "which scan/import operation is current." matterIdRef alone cannot
  // catch an A -> B -> A sequence: if the user returns to the same matter id, a still-pending
  // operation from the *original* visit to A would again see a matching matter id and could
  // revive itself over a newer A operation's results. Every matter-identity reset and every new
  // scan/import bumps this token, so a captured (matterId, operation) pair can only ever belong
  // to the single most recent operation for that matter -- never a resurrected earlier one.
  const operationIdRef = useRef(0);
  const retryButtonRef = useRef<HTMLButtonElement | null>(null);
  const resultSummaryRef = useRef<HTMLParagraphElement | null>(null);
  const supported = isDirectoryDiscoverySupported();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      scanControllerRef.current?.abort();
    };
  }, []);

  // A discovered candidate is scoped to the matter that was current when the folder was
  // scanned (its match reasons, existing-document comparison and import target all derive
  // from that matter). Reusing this component across a matter switch -- rather than
  // unmounting/remounting it -- must never let a result found under one matter stay visible,
  // selectable, or importable once the matter prop changes to another.
  useEffect(() => {
    operationIdRef.current += 1;
    scanControllerRef.current?.abort();
    scanControllerRef.current = null;
    setStatus({step: 'idle'});
    setSelectedIds(new Set());
    setError('');
    setNote('');
    setPickerRecovery(false);
  }, [matter.id]);

  useEffect(() => {
    if (pickerRecovery) retryButtonRef.current?.focus();
  }, [pickerRecovery]);

  useEffect(() => {
    if (status.step === 'reviewing' || status.step === 'done') resultSummaryRef.current?.focus();
  }, [status.step]);

  async function startScan() {
    // Captured once, up front: this is the matter this entire scan operation belongs to, no
    // matter how long the (unabortable) native picker takes to resolve or how many times this
    // component re-renders with a different matter in the meantime.
    const scanMatterId = matter.id;
    const myOperation = (operationIdRef.current += 1);
    const stale = () => !mountedRef.current || matterIdRef.current !== scanMatterId || operationIdRef.current !== myOperation;
    setError('');
    setNote('');
    setPickerRecovery(false);
    let root: FileSystemDirectoryHandle;
    try {
      root = await window.showDirectoryPicker!();
    } catch (thrown) {
      if (stale()) return;
      setPickerRecovery(true);
      if (thrown instanceof DOMException && thrown.name === 'AbortError') {
        setNote('No folder was selected. Nothing was added.');
      } else {
        setError('No folder was selected, so nothing was added. Choose another folder or subfolder you control.');
      }
      return;
    }
    if (stale()) return;
    const controller = new AbortController();
    scanControllerRef.current = controller;
    setStatus({step: 'scanning', filesExamined: 0});
    setSelectedIds(new Set());
    try {
      const result = await scanDirectoryForCandidates(
        root,
        {
          name: matter.name,
          reference: matter.reference,
          existingDocumentNames: matter.documents.map((document) => document.name),
          knownDates: [matter.createdAt, ...matter.documents.map((document) => document.addedAt)],
        },
        (filesExamined) => {
          if (!stale()) setStatus({step: 'scanning', filesExamined});
        },
        controller.signal,
      );
      if (stale()) return;
      if (result.cancelled) {
        setNote(`Search cancelled -- ${formatScanStats(result.stats)}`);
        setStatus({step: 'idle'});
        return;
      }
      setStatus({step: 'reviewing', folderName: root.name, candidates: result.candidates, truncated: result.truncated, stats: result.stats});
    } catch {
      if (stale()) return;
      setError('The scan could not be completed. Try again.');
      setStatus({step: 'idle'});
    } finally {
      if (matterIdRef.current === scanMatterId && operationIdRef.current === myOperation) scanControllerRef.current = null;
    }
  }

  function cancelScan() {
    scanControllerRef.current?.abort();
  }

  function toggleSelected(id: string) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function importSelected() {
    if (status.step !== 'reviewing') return;
    // Captured once, up front: this import belongs to the matter displayed when it was started.
    // If the matter switches mid-import, the underlying addDocument/registerDocumentBytes calls
    // below stay correctly bound to this matter id (they never read the live `matter` prop), but
    // this component's own visible status/selection must stop following an import that is no
    // longer for the matter currently on screen.
    const importMatterId = matter.id;
    const myOperation = (operationIdRef.current += 1);
    const stale = () => !mountedRef.current || matterIdRef.current !== importMatterId || operationIdRef.current !== myOperation;
    const stats = status.stats;
    const candidates = status.candidates.filter((candidate) => selectedIds.has(candidate.id));
    if (candidates.length === 0) return;
    setError('');
    const folderName = status.folderName;
    setStatus({step: 'importing', folderName, total: candidates.length, completed: 0, currentName: candidates[0].name});
    let currentVersion = matter.version;
    let imported = 0;
    let skipped = 0;
    for (const candidate of candidates) {
      if (!stale()) {
        setStatus({step: 'importing', folderName, total: candidates.length, completed: imported + skipped, currentName: candidate.name});
      }
      if (candidate.file.size > FENZSAFE_DOCUMENT_MAX_BYTES) {
        skipped += 1;
        continue;
      }
      try {
        const afterAdd = await fenzsafeStore.addDocument(
          importMatterId,
          currentVersion,
          {name: candidate.name, category: 'Other', source: 'upload', sizeKb: Math.max(1, Math.round(candidate.sizeBytes / 1024))},
          crypto.randomUUID(),
        );
        currentVersion = afterAdd.version;
        const created = afterAdd.documents[afterAdd.documents.length - 1];
        const afterRegister = await fenzsafeStore.registerDocumentBytes(importMatterId, currentVersion, created.id, candidate.file, crypto.randomUUID());
        currentVersion = afterRegister.version;
        imported += 1;
      } catch (thrown) {
        if (!stale()) {
          setError(friendlyMutationError(thrown, `Could not import ${candidate.name}. The rest of the selection was skipped.`));
        }
        skipped += candidates.length - imported - skipped;
        break;
      }
    }
    if (!stale()) {
      setStatus({step: 'done', selected: candidates.length, imported, skipped, stats});
      setSelectedIds(new Set());
    }
  }

  return (
    <section className="fsa-panel" aria-labelledby="fsa-discovery-heading">
      <h2 id="fsa-discovery-heading">Find related files</h2>
      {!supported && (
        <p className="fsa-package-status">
          This browser can&rsquo;t search a folder for related files. Try the latest Chrome or Edge, or add files individually above.
        </p>
      )}
      {supported && (
        <p className="fsa-package-status">
          Choose a folder or subfolder you control. Your browser may block protected folders. FenzSafe checks file names and, for eligible TXT,
          CSV, PDF, EML, and DOCX files, their contents inside the folder you choose. This user-started search stays on this device, and nothing
          is added until you select files and confirm.
        </p>
      )}
      {supported && status.step === 'idle' && (
        <>
          {note && (
            <p className="fsa-package-status" role="status">
              {note}
            </p>
          )}
          <div className="fsa-actions">
            <button ref={retryButtonRef} type="button" className="fsa-primary" onClick={() => void startScan()}>
              {pickerRecovery ? 'Choose another folder' : 'Choose a folder to search'}
            </button>
          </div>
        </>
      )}
      {status.step === 'scanning' && (
        <>
          <p className="fsa-package-status" role="status">
            Searching&hellip; {status.filesExamined} file{status.filesExamined === 1 ? '' : 's'} examined so far.
          </p>
          <div className="fsa-actions">
            <button type="button" className="fsa-secondary" onClick={cancelScan}>
              Cancel search
            </button>
          </div>
        </>
      )}
      {status.step === 'reviewing' && (
        <>
          <p ref={resultSummaryRef} className="fsa-package-status" role="status" tabIndex={-1}>
            Searched &ldquo;{status.folderName}&rdquo; and found {status.candidates.length} possible match{status.candidates.length === 1 ? '' : 'es'}.
            {status.truncated && ' This folder has a lot of files, so the search stopped early.'}
          </p>
          <p className="fsa-package-status">{formatScanStats(status.stats)}</p>
          {status.candidates.length === 0 ? (
            <div className="fsa-actions">
              <button type="button" className="fsa-secondary" onClick={() => setStatus({step: 'idle'})}>
                Search a different folder
              </button>
            </div>
          ) : (
            <>
              <ul className="fsa-discovery-list">
                {status.candidates.map((candidate) => (
                  <li key={candidate.id}>
                    <label className="fsa-checkbox-row">
                      <input type="checkbox" checked={selectedIds.has(candidate.id)} onChange={() => toggleSelected(candidate.id)} />
                      <span>
                        <strong>{candidate.name}</strong> <span className="fsa-discovery-band">{BAND_LABEL[candidate.band]}</span>
                        <br />
                        <span className="fsa-discovery-path">{candidate.path}</span>
                        <ul className="fsa-discovery-reasons">
                          {candidate.reasons.map((reason) => (
                            <li key={reason.kind + reason.detail}>{reason.detail}</li>
                          ))}
                        </ul>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
              <div className="fsa-actions">
                <button type="button" className="fsa-secondary" onClick={() => setStatus({step: 'idle'})}>
                  Search a different folder
                </button>
                <button type="button" className="fsa-primary" onClick={() => void importSelected()} disabled={selectedIds.size === 0}>
                  Add {selectedIds.size} selected to FenzSafe
                </button>
              </div>
            </>
          )}
        </>
      )}
      {status.step === 'importing' && (
        <p className="fsa-package-status" role="status">
          Adding files from &ldquo;{status.folderName}&rdquo;&hellip; {status.completed} of {status.total} ({status.currentName}).
        </p>
      )}
      {status.step === 'done' && (
        <>
          <p ref={resultSummaryRef} className="fsa-package-status" role="status" tabIndex={-1}>
            {status.imported} of {status.selected} selected file{status.selected === 1 ? '' : 's'} added to this matter.
            {status.skipped > 0 && ` ${status.skipped} skipped (too large or could not be added).`}
          </p>
          <p className="fsa-package-status">{formatScanStats(status.stats)}</p>
          <div className="fsa-actions">
            <button type="button" className="fsa-secondary" onClick={() => setStatus({step: 'idle'})}>
              Search again
            </button>
          </div>
        </>
      )}
      {error && <p className="fsa-field-error" role="alert">{error}</p>}
    </section>
  );
}
